<script setup lang="ts">
  /**
   * RankedList — Yahoo-style Trending module: numbered stories with
   * thumbnails and optional highlight badges.
   *
   * Rows open in a new tab and leave the portal untouched, matching the
   * platform's own content navigation (useRedirectActions navigates with
   * `{ open: { target: '_blank' } }`). rel stays at `noopener` rather than
   * adding `noreferrer`, because these are our own same-origin article
   * pages and they should still see the referrer.
   */
  import { resolveComponent } from 'vue'
  import type { RankedListProps } from './types'

  withDefaults(defineProps<RankedListProps>(), {
    heading: 'Trending',
    headingIcon: 'i-heroicons-bolt',
    items: () => [],
  })

  const NuxtLink = resolveComponent('NuxtLink')
</script>

<template>
  <section
    data-portal-module="trending"
    class="rounded-lg bg-surface-primary py-2"
  >
    <div class="flex items-center gap-2 px-4 pb-1 pt-3">
      <UIcon
        v-if="headingIcon"
        :name="headingIcon"
        class="h-4 w-4 shrink-0 text-content-primary"
      />
      <h2 class="text-[17px] font-bold text-content-primary">
        {{ heading }}
      </h2>
    </div>

    <ol class="flex flex-col divide-y divide-[var(--yp-divider)] px-4">
      <li v-for="(item, index) in items" :key="item.id">
        <component
          :is="item.articleUrl ? NuxtLink : 'div'"
          :to="item.articleUrl"
          :target="item.articleUrl ? '_blank' : undefined"
          :rel="item.articleUrl ? 'noopener' : undefined"
          class="group flex items-center gap-3 py-3"
          :class="item.articleUrl ? 'cursor-pointer' : ''"
        >
          <div
            v-if="item.imageUrl"
            class="relative aspect-[4/3] w-20 shrink-0 overflow-hidden rounded-md"
          >
            <img
              :src="item.imageUrl"
              alt=""
              class="h-full w-full object-cover"
              loading="lazy"
            />
            <span
              class="absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-br-md bg-white text-[11px] font-bold text-black"
            >
              {{ index + 1 }}
            </span>
          </div>
          <span
            v-else
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--yp-chip)] text-xs font-bold text-content-primary"
          >
            {{ index + 1 }}
          </span>

          <span class="min-w-0 flex-1">
            <span
              class="line-clamp-2 text-[14px] font-semibold leading-snug text-content-primary group-hover:underline"
            >
              {{ item.title }}
            </span>
          </span>

          <span
            v-if="item.badge"
            class="shrink-0 rounded-sm bg-[#f5e14b] px-1.5 py-px text-[10px] font-bold text-black"
          >
            {{ item.badge }}
          </span>
        </component>
      </li>
    </ol>
  </section>
</template>
